import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProject, publishProject } from '../../services/projects';
import { getProjectPages } from '../../services/pages';
import AppHeader from '../../components/AppHeader';
import ContextualActionBar from '../../components/ContextualActionBar';
import DeviceToolbar from './components/DeviceToolbar';
import PreviewFrame from './components/PreviewFrame';
import FeedbackPanel from './components/FeedbackPanel';
import { getSectionComponent } from '../../sections';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import config from '../../config';
import { useTheme } from '../../hooks/useTheme';

const PreviewMode = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const [currentDevice, setCurrentDevice] = useState('desktop');
  const [isRotated, setIsRotated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Загрузка секций
  useEffect(() => {
    async function loadData() {
      // Сначала пробуем localStorage (для быстрого превью из page-builder)
      const previewSections = localStorage.getItem('previewSections');
      if (previewSections) {
        try {
          setSections(JSON.parse(previewSections));
          // Очищаем после использования
          localStorage.removeItem('previewSections');
        } catch (e) {
          console.error('Failed to parse preview sections:', e);
        }
      }

      // Если есть projectId, загружаем данные из Firestore
      if (projectId) {
        try {
          const project = await getProject(projectId);
          setCurrentProject(project);

          // Если не было данных из localStorage, берём из Firestore
          if (!previewSections) {
            const pages = await getProjectPages(projectId);
            if (pages.length > 0) {
              setSections(pages[0].sections || []);
            }
          }
        } catch (error) {
          console.error('Error loading project:', error);
        }
      }

      setLoading(false);
    }
    loadData();
  }, [projectId]);

  const handleDeviceChange = (device) => {
    setCurrentDevice(device);
    setIsRotated(false);
  };

  const handleRotate = () => {
    setIsRotated(!isRotated);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBackToEditor = () => {
    if (projectId) {
      navigate(`/projects/${projectId}/editor`);
    } else {
      navigate('/dashboard');
    }
  };

  const getPublishedUrl = (project) => {
    if (!project) return null;

    const domainType = project.domainType || project.settings?.domainType;
    const customDomain = project.customDomain || project.settings?.customDomain;
    const subdomain = project.subdomain || project.settings?.subdomain;
    const selectedDomain = project.selectedDomain || project.settings?.selectedDomain;

    if (domainType === 'custom' && customDomain) {
      return `https://${customDomain}`;
    }

    if (subdomain) {
      const domain = selectedDomain || config.baseDomain;
      return `https://${subdomain}.${domain}`;
    }

    return null;
  };

  const handlePublish = async () => {
    if (!projectId) {
      alert('Сначала сохраните сайт');
      return;
    }

    setIsSaving(true);
    try {
      const publishedPages = [{
        id: 'main',
        name: 'Главная',
        sections: sections
      }];

      await publishProject(projectId, publishedPages);

      // Reload project to get updated data
      const updatedProject = await getProject(projectId);
      setCurrentProject(updatedProject);

      const url = getPublishedUrl(updatedProject);
      if (url) {
        setPublishedUrl(url);
        setShowPublishModal(true);
      } else {
        alert('Сайт опубликован!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Ошибка публикации');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyUrl = () => {
    if (publishedUrl) {
      navigator.clipboard.writeText(publishedUrl);
    }
  };

  const handleClosePublishModal = () => {
    setShowPublishModal(false);
    setPublishedUrl(null);
  };

  // Компонент для рендера секций
  const PreviewContent = () => {
    const themeKey = currentProject?.settings?.theme || 'default';
    const themeStyles = useTheme(themeKey);

    if (sections.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background" style={themeStyles}>
          <div className="text-center p-8">
            <Icon name="FileX" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
            <p className="text-text-secondary">Страница пуста</p>
            <p className="text-sm text-text-secondary mt-2">Добавьте секции в редакторе</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background" style={themeStyles}>
        {sections.map((section, index) => {
          const SectionComponent = getSectionComponent(section.type);
          if (SectionComponent) {
            return (
              <div key={section.id || index} id={`section-${index}`}>
                <SectionComponent
                  data={section.content || {}}
                  styles={section.styles || {}}
                  theme={themeKey}
                />
              </div>
            );
          }
          // Fallback для неизвестных секций
          return (
            <div
              key={section.id || index}
              id={`section-${index}`}
              className="p-8 bg-muted border-b border-border text-center"
            >
              <Icon name="AlertCircle" size={24} className="mx-auto mb-2 text-warning" />
              <p className="text-sm text-text-secondary">
                Секция "{section.name || section.type}" не найдена в реестре
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const warnings = [];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
        currentProject={currentProject}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col mt-14 lg:mt-[104px]">
        <DeviceToolbar
          currentDevice={currentDevice}
          onDeviceChange={handleDeviceChange}
          onRotate={handleRotate}
          isRotated={isRotated}
        />

        <PreviewFrame
          currentDevice={currentDevice}
          isRotated={isRotated}
          content={<PreviewContent />}
        />

        <FeedbackPanel
          warnings={warnings}
          currentDevice={currentDevice}
        />

        <ContextualActionBar
          onSave={handleBackToEditor}
          onPreview={() => {}}
          onPublish={handlePublish}
          onExport={() => {}}
          isSaving={isSaving}
          hasUnsavedChanges={false}
          publishStatus={currentProject?.status || 'draft'}
        />
      </div>

      {/* Publish Success Modal */}
      {showPublishModal && publishedUrl && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClosePublishModal}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md p-6 bg-card rounded-xl border border-border shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Сайт опубликован!
              </h2>
              <p className="text-sm text-text-secondary mb-6">
                Ваш сайт теперь доступен по адресу:
              </p>

              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg mb-6">
                <Icon name="Globe" size={20} className="text-primary flex-shrink-0" />
                <a
                  href={publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline truncate flex-1 text-left"
                >
                  {publishedUrl}
                </a>
                <button
                  onClick={handleCopyUrl}
                  className="p-2 hover:bg-background rounded-lg transition-colors flex-shrink-0"
                  title="Скопировать ссылку"
                >
                  <Icon name="Copy" size={16} className="text-text-secondary" />
                </button>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleClosePublishModal}
                >
                  Закрыть
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => window.open(publishedUrl, '_blank')}
                >
                  Открыть сайт
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PreviewMode;